/* Hachi — immersive motion engine (GSAP + ScrollTrigger + Lenis)
   Award-site scroll experience. Degrades gracefully: no JS / no GSAP / reduced-motion
   all leave content fully visible. */
(function () {
  "use strict";
  var RM = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var docEl = document.documentElement;

  function ready(fn) {
    if (document.readyState !== "loading") fn();
    else document.addEventListener("DOMContentLoaded", fn);
  }

  ready(function () {
    var hasGSAP = !!(window.gsap && window.ScrollTrigger);
    docEl.classList.add("hg-motion-ready");

    // Material depth: grain + vignette overlay (texture, not motion — applies always)
    if (document.body && !document.querySelector(".hg-fx")) {
      var fx = document.createElement("div");
      fx.className = "hg-fx"; fx.setAttribute("aria-hidden", "true");
      fx.innerHTML = '<span class="hg-fx__grain"></span><span class="hg-fx__vignette"></span>';
      document.body.appendChild(fx);
    }

    // Announcement bar → apply page (only if it has no link of its own)
    var hgAnn = document.querySelector(".announcement-bar, .header-announcements");
    if (hgAnn) hgAnn.addEventListener("click", function (e) {
      if (e.target.closest("a, button")) return;
      window.location.href = "/pages/become-our-partner";
    });
    if (RM || !hasGSAP) {
      // reveal-safe: show everything, run no scroll motion
      docEl.classList.add("hg-motion-off");
      initCountersFallback();
      return;
    }
    var gsap = window.gsap, ScrollTrigger = window.ScrollTrigger;
    gsap.registerPlugin(ScrollTrigger);

    /* ---------- Lenis smooth scroll ---------- */
    if (window.Lenis) {
      // Horizon scrolls inside `.page-wrapper` (a div), NOT the window. Detect the real
      // scroller so Lenis AND ScrollTrigger both drive it — otherwise wheel + scroll
      // animations are dead (they'd watch a window that never scrolls).
      var scrollEl = document.querySelector(".page-wrapper");
      var useEl = !!(scrollEl && scrollEl.scrollHeight > scrollEl.clientHeight + 4);
      var baseOpts = {
        duration: 1.05,
        easing: function (t) { return Math.min(1, 1.001 - Math.pow(2, -10 * t)); },
        smoothWheel: true, touchMultiplier: 1.6
      };
      if (useEl) { baseOpts.wrapper = scrollEl; baseOpts.content = scrollEl; }
      var lenis = new Lenis(baseOpts);
      window.__hgLenis = lenis;
      docEl.classList.add("lenis");

      if (useEl) {
        ScrollTrigger.scrollerProxy(scrollEl, {
          scrollTop: function (value) {
            if (arguments.length) { lenis.scrollTo(value, { immediate: true }); }
            return lenis.scroll;
          },
          getBoundingClientRect: function () {
            return { top: 0, left: 0, width: window.innerWidth, height: window.innerHeight };
          }
        });
        ScrollTrigger.defaults({ scroller: scrollEl });
      }
      lenis.on("scroll", ScrollTrigger.update);
      ScrollTrigger.addEventListener("refresh", function () { lenis.resize(); });

      function hgRaf(time) { lenis.raf(time); requestAnimationFrame(hgRaf); }
      requestAnimationFrame(hgRaf);
      gsap.ticker.lagSmoothing(0);

      // recalc after fonts/images (the tall Nova image loads late) so the wheel range is right
      window.addEventListener("load", function () { lenis.resize(); ScrollTrigger.refresh(); });
      setTimeout(function () { lenis.resize(); ScrollTrigger.refresh(); }, 900);
      // keep Lenis height in sync as late media changes layout (so the footer is always reachable)
      if (window.ResizeObserver) {
        var hgRO = new ResizeObserver(function () { lenis.resize(); ScrollTrigger.refresh(); });
        [document.querySelector("#MainContent"), scrollEl.lastElementChild, scrollEl.firstElementChild]
          .forEach(function (n) { if (n) hgRO.observe(n); });
      }
      [250, 600, 1000, 1600, 2500, 4000].forEach(function (t) {
        setTimeout(function () { lenis.resize(); ScrollTrigger.refresh(); }, t);
      });

      // anchor links → smooth
      document.addEventListener("click", function (e) {
        var a = e.target.closest('a[href^="#"]');
        if (!a) return;
        var id = a.getAttribute("href");
        if (id.length > 1 && document.querySelector(id)) {
          e.preventDefault();
          lenis.scrollTo(id, { offset: -80 });
        }
      });
    }

    /* ---------- Hero intro (plays on LOAD, never scroll — above-the-fold safe) ---------- */
    var heroIntro = document.querySelector("[data-hero-intro]");
    if (heroIntro) {
      var hWords = [];
      heroIntro.querySelectorAll("[data-split]").forEach(function (el) {
        hWords = hWords.concat(splitEl(el) || []);
      });
      var hReveal = [];
      heroIntro.querySelectorAll("[data-reveal]").forEach(function (el) {
        var kids = el.hasAttribute("data-reveal-stagger") ? [].slice.call(el.children) : [el];
        hReveal = hReveal.concat(kids);
      });
      gsap.set(hWords, { yPercent: 118, rotate: 2 });
      gsap.set(heroIntro.querySelectorAll("[data-split]"), { opacity: 1 });
      gsap.set(hReveal, { y: 30, opacity: 0 });
      gsap.timeline({ delay: 0.2 })
        .to(hWords, { yPercent: 0, rotate: 0, duration: 1.1, ease: "power4.out", stagger: 0.07 }, 0.05)
        .to(hReveal, { y: 0, opacity: 1, duration: 0.9, ease: "power3.out", stagger: 0.08 }, 0.3);
    }

    /* ---------- Hero spatial carousel: scroll-driven revolve, from-bg-to-podium, mirrored back/next ---------- */
    (function () {
      var hero = document.querySelector("[data-hero-carousel]");
      if (!hero) return;
      var shears = [].slice.call(hero.querySelectorAll(".hg-hero2__shear"));
      var slides = [].slice.call(hero.querySelectorAll(".hg-hero2__slide"));
      var dots = [].slice.call(hero.querySelectorAll(".hg-hero2__dot"));
      var idxEl = hero.querySelector("[data-hero-index]");
      var capEl = hero.querySelector("[data-hero-caption]");
      var n = Math.min(shears.length, slides.length);
      if (n < 1) return;
      var titles = slides.map(function (s) { return s.querySelector(".hg-hero2__title"); });
      var subs = slides.map(function (s) { return s.querySelector(".hg-hero2__sub"); });
      var words = titles.map(function (t) { return t ? (splitEl(t) || []) : []; });
      gsap.set(titles, { opacity: 1 });

      var current = 0, animating = false, desired = 0;

      slides.forEach(function (s, i) { gsap.set(s, { autoAlpha: i === 0 ? 1 : 0 }); });
      words.forEach(function (w) { gsap.set(w, { yPercent: 115, opacity: 1 }); });
      shears.forEach(function (el, i) {
        gsap.set(el, i === 0
          ? { rotateY: 0, xPercent: 0, z: 0, scale: 1, opacity: 1 }
          : { rotateY: -64, xPercent: 72, z: -420, scale: 0.64, opacity: 0 });
      });
      if (capEl && shears[0]) capEl.textContent = shears[0].getAttribute("data-caption") || "";

      // load entrance for slide 0
      gsap.to(words[0], { yPercent: 0, duration: 1.1, stagger: 0.06, ease: "power4.out", delay: 0.25 });
      if (subs[0]) gsap.from(subs[0], { opacity: 0, y: 18, duration: 0.9, delay: 0.5, ease: "power3.out" });

      // dir: +1 = next (scroll down), -1 = back (scroll up) — mirrored transition
      function goTo(next, dir) {
        next = Math.max(0, Math.min(n - 1, next));
        if (next === current || n < 2) return;
        var s = dir || (next > current ? 1 : -1);
        var outI = current, inI = next;
        animating = true; current = next;
        // text out — mirrors direction
        gsap.to(words[outI], { yPercent: -110 * s, opacity: 0, duration: 0.45, stagger: 0.02, ease: "power2.in" });
        gsap.to(slides[outI], { autoAlpha: 0, duration: 0.4, delay: 0.2 });
        // shear OUT — revolves away to the side, swings back into the depth and fades
        gsap.to(shears[outI], { xPercent: -74 * s, z: -320, rotateY: 64 * s, scale: 0.7, opacity: 0,
          duration: 0.85, ease: "power2.inOut" });
        // shear IN — revolves in from the OPPOSITE side + background, arcing forward to the podium
        gsap.set(shears[inI], { xPercent: 74 * s, z: -420, rotateY: -64 * s, scale: 0.64, opacity: 0 });
        gsap.to(shears[inI], { xPercent: 0, z: 0, rotateY: 0, scale: 1, opacity: 1,
          duration: 1.15, ease: "power3.out", delay: 0.1, onComplete: function () {
            animating = false;
            if (desired !== current) goTo(desired, desired > current ? 1 : -1);
          } });
        // text in — re-runs the entrance, mirrored
        gsap.set(slides[inI], { autoAlpha: 1 });
        gsap.set(words[inI], { yPercent: 115 * s, opacity: 1 });
        gsap.to(words[inI], { yPercent: 0, duration: 0.9, stagger: 0.05, ease: "power4.out", delay: 0.28 });
        if (subs[inI]) gsap.fromTo(subs[inI], { opacity: 0, y: 16 * s }, { opacity: 0.82, y: 0, duration: 0.8, delay: 0.42, ease: "power3.out" });
        if (capEl) capEl.textContent = shears[inI].getAttribute("data-caption") || "";
        if (idxEl) idxEl.textContent = ("0" + (inI + 1)).slice(-2);
        dots.forEach(function (d, i) { d.classList.toggle("is-active", i === inI); });
      }

      // Scroll drives it on EVERY viewport (mobile + desktop): pin the hero, map scroll → active shear
      var st = null;
      if (n > 1) {
        hero.classList.add("is-scroll");
        st = ScrollTrigger.create({
          trigger: hero, start: "top top", end: "bottom bottom",
          onUpdate: function (self) {
            desired = Math.max(0, Math.min(n - 1, Math.round(self.progress * (n - 1))));
            if (desired !== current && !animating) goTo(desired, desired > current ? 1 : -1);
          }
        });
        dots.forEach(function (d, i) {
          d.addEventListener("click", function () {
            if (!st || !window.__hgLenis) return;
            var p = n > 1 ? i / (n - 1) : 0;
            window.__hgLenis.scrollTo(st.start + p * (st.end - st.start) + 6);
          });
        });
      }

      /* Interactive parallax: the shear follows the cursor (desktop) or device tilt/gyro (mobile) */
      var pStage = hero.querySelector(".hg-hero2__stage");
      if (pStage) {
        var pqx = gsap.quickTo(pStage, "x", { duration: 0.5, ease: "power2.out" });
        var pqy = gsap.quickTo(pStage, "y", { duration: 0.5, ease: "power2.out" });
        var pApply = function (nx, ny) {
          nx = Math.max(-1, Math.min(1, nx)); ny = Math.max(-1, Math.min(1, ny));
          pqx(nx * 30); pqy(ny * 22);
        };
        if (window.matchMedia("(pointer: fine)").matches) {
          hero.addEventListener("mousemove", function (e) {
            var r = hero.getBoundingClientRect();
            pApply((e.clientX - r.left) / r.width * 2 - 1, (e.clientY - r.top) / r.height * 2 - 1);
          });
          hero.addEventListener("mouseleave", function () { pqx(0); pqy(0); });
        } else {
          // Touch devices: the shear follows the finger — NO motion permission, no prompt (clean on iOS Safari)
          hero.addEventListener("touchmove", function (e) {
            var t = e.touches && e.touches[0]; if (!t) return;
            var r = hero.getBoundingClientRect();
            pApply(((t.clientX - r.left) / r.width * 2 - 1) * 0.6, ((t.clientY - r.top) / r.height * 2 - 1) * 0.55);
          }, { passive: true });
          hero.addEventListener("touchend", function () { pqx(0); pqy(0); });
        }
      }
    })();

    /* ---------- Split text into masked words ---------- */
    function splitEl(el) {
      if (el.dataset.hgSplit === "done") return;
      var out = [];
      (function walk(node, accent) {
        Array.prototype.slice.call(node.childNodes).forEach(function (child) {
          if (child.nodeType === 3) {
            var parts = child.textContent.split(/(\s+)/);
            var frag = document.createDocumentFragment();
            parts.forEach(function (p) {
              if (p === "") return;
              if (/^\s+$/.test(p)) { frag.appendChild(document.createTextNode(p)); return; }
              var mask = document.createElement("span");
              mask.className = "hg-w" + (accent ? " hg-w--accent" : "");
              var inner = document.createElement("span");
              inner.className = "hg-w-i";
              inner.textContent = p;
              mask.appendChild(inner);
              frag.appendChild(mask);
              out.push(inner);
            });
            node.replaceChild(frag, child);
          } else if (child.nodeType === 1) {
            if (child.tagName === "BR") return;
            walk(child, accent || child.tagName === "EM" || child.classList.contains("accent"));
          }
        });
      })(el, false);
      el.dataset.hgSplit = "done";
      return out;
    }

    document.querySelectorAll("[data-split]").forEach(function (el) {
      if (el.closest("[data-hero-intro]")) return;
      var words = splitEl(el);
      if (!words || !words.length) return;
      gsap.set(el, { opacity: 1 });
      gsap.set(words, { yPercent: 118, rotate: 2 });
      gsap.to(words, {
        yPercent: 0, rotate: 0, ease: "power4.out", duration: 1.05,
        stagger: 0.045,
        scrollTrigger: { trigger: el, start: "top 88%" }
      });
    });

    /* ---------- Auto split-reveal on section headings (same voice as the hero) ---------- */
    document.querySelectorAll(".hg-value__title, .hg-proc__title, .hg-tst__title, .hg-aud__title, .hg-hrange__title, .hg-pp__title, .hg-pp-band__title, .hg-split-title").forEach(function (el) {
      if (el.closest("[data-hero-intro]") || el.closest("[data-forge]")) return;
      var words = splitEl(el);
      if (!words || !words.length) return;
      gsap.set(el, { opacity: 1 });
      gsap.set(words, { yPercent: 115 });
      gsap.to(words, {
        yPercent: 0, ease: "power4.out", duration: 1, stagger: 0.05,
        scrollTrigger: { trigger: el, start: "top 86%" }
      });
    });

    /* ---------- Reveal (fade + rise), optional stagger ---------- */
    document.querySelectorAll("[data-reveal]").forEach(function (el) {
      if (el.closest("[data-hero-intro]")) return;
      var kids = el.hasAttribute("data-reveal-stagger")
        ? el.children : [el];
      gsap.set(kids, { y: 42, opacity: 0 });
      gsap.to(kids, {
        y: 0, opacity: 1, ease: "power3.out", duration: 0.9,
        stagger: el.hasAttribute("data-reveal-stagger") ? 0.09 : 0,
        scrollTrigger: { trigger: el, start: "top 86%" }
      });
    });

    /* ---------- Clip / image mask reveal ---------- */
    document.querySelectorAll("[data-mask]").forEach(function (el) {
      gsap.fromTo(el,
        { clipPath: "inset(0 0 100% 0)" },
        { clipPath: "inset(0 0 0% 0)", ease: "power3.out", duration: 1.2,
          scrollTrigger: { trigger: el, start: "top 85%" } });
      var img = el.querySelector("img");
      if (img) gsap.fromTo(img, { scale: 1.25 }, { scale: 1, ease: "power3.out", duration: 1.4,
        scrollTrigger: { trigger: el, start: "top 85%" } });
    });

    /* ---------- Parallax ---------- */
    document.querySelectorAll("[data-parallax]").forEach(function (el) {
      var speed = parseFloat(el.dataset.parallax) || 0.2;
      gsap.to(el, {
        yPercent: -speed * 100, ease: "none",
        scrollTrigger: { trigger: el.dataset.parallaxTrigger ? el.closest(el.dataset.parallaxTrigger) : el,
          start: "top bottom", end: "bottom top", scrub: true }
      });
    });

    /* ---------- Forge scrollytelling (CSS-sticky pin + scrubbed story) ---------- */
    document.querySelectorAll("[data-forge]").forEach(function (sec) {
      var steps = sec.querySelectorAll(".hg-forge__step");
      var shear = sec.querySelector(".hg-forge__shear");
      var fill = sec.querySelector(".hg-forge__progress-fill");
      var n = steps.length || 1;
      var lastIdx = -1;
      ScrollTrigger.create({
        trigger: sec, start: "top top", end: "bottom bottom", scrub: true,
        onUpdate: function (self) {
          var p = self.progress;
          var idx = Math.max(0, Math.min(n - 1, Math.floor(p * n - 0.0001)));
          if (idx !== lastIdx) {
            steps.forEach(function (s, i) { s.classList.toggle("is-active", i === idx); });
            lastIdx = idx;
          }
          // starts at 0° (matches the at-rest shear) and tilts smoothly as you scroll — no snap
          if (shear) gsap.set(shear, { rotate: p * 12, scale: 1 + p * 0.08, yPercent: p * 6 });
          if (fill) fill.style.transform = "scaleX(" + p + ")";
        }
      });

      /* Subtle cursor/touch parallax on the forge shear — on the media container (separate from the scroll-tilt) */
      var fMedia = sec.querySelector(".hg-forge__media");
      var fSticky = sec.querySelector(".hg-forge__sticky");
      if (fMedia && fSticky) {
        var fqx = gsap.quickTo(fMedia, "x", { duration: 0.6, ease: "power2.out" });
        var fqy = gsap.quickTo(fMedia, "y", { duration: 0.6, ease: "power2.out" });
        var fApply = function (nx, ny) {
          nx = Math.max(-1, Math.min(1, nx)); ny = Math.max(-1, Math.min(1, ny));
          fqx(nx * 20); fqy(ny * 14);
        };
        if (window.matchMedia("(pointer: fine)").matches) {
          fSticky.addEventListener("mousemove", function (e) {
            var r = fSticky.getBoundingClientRect();
            fApply((e.clientX - r.left) / r.width * 2 - 1, (e.clientY - r.top) / r.height * 2 - 1);
          });
          fSticky.addEventListener("mouseleave", function () { fqx(0); fqy(0); });
        } else {
          fSticky.addEventListener("touchmove", function (e) {
            var t = e.touches && e.touches[0]; if (!t) return;
            var r = fSticky.getBoundingClientRect();
            fApply(((t.clientX - r.left) / r.width * 2 - 1) * 0.55, ((t.clientY - r.top) / r.height * 2 - 1) * 0.5);
          }, { passive: true });
          fSticky.addEventListener("touchend", function () { fqx(0); fqy(0); });
        }
      }
    });

    /* ---------- Horizontal-scroll range (sticky pin + scrubbed x) ---------- */
    document.querySelectorAll("[data-hscroll]").forEach(function (sec) {
      var track = sec.querySelector(".hg-hrange__track");
      var vp = sec.querySelector(".hg-hrange__viewport");
      if (!track || !vp) return;
      gsap.to(track, {
        x: function () { return -Math.max(0, track.scrollWidth - vp.clientWidth); },
        ease: "none",
        scrollTrigger: {
          trigger: sec, start: "top top", end: "bottom bottom",
          scrub: 0.4, invalidateOnRefresh: true
        }
      });
    });

    /* ---------- Counters ---------- */
    document.querySelectorAll("[data-count]").forEach(function (el) {
      var target = parseFloat(el.dataset.count);
      var obj = { v: 0 };
      ScrollTrigger.create({
        trigger: el, start: "top 90%", once: true,
        onEnter: function () {
          gsap.to(obj, { v: target, duration: 1.8, ease: "power2.out",
            onUpdate: function () {
              el.textContent = Math.round(obj.v).toLocaleString();
            } });
        }
      });
    });

    /* ---------- Velocity-reactive marquee ---------- */
    document.querySelectorAll("[data-marquee]").forEach(function (track) {
      var dir = track.dataset.marqueeDir === "rtl" ? 1 : -1;
      var base = (parseFloat(track.dataset.marqueeSpeed) || 40); // px/s
      var x = 0, w = track.scrollWidth / 2, vel = 0;
      if (window.__hgLenis) window.__hgLenis.on("scroll", function (e) {
        vel = (e.velocity || 0);
      });
      gsap.ticker.add(function () {
        var boost = 1 + Math.min(Math.abs(vel) / 12, 6);
        x += dir * (base * boost) * (1 / 60);
        if (x <= -w) x += w; if (x >= 0 && dir === 1) x -= w;
        track.style.transform = "translateX(" + x + "px)";
        vel *= 0.9;
      });
    });

    /* ---------- Magnetic elements ---------- */
    if (window.matchMedia("(pointer:fine)").matches) {
      document.querySelectorAll("[data-magnetic], a.button, a.hg-btn:not(.hg-btn--ghost)").forEach(function (el) {
        var str = parseFloat(el.dataset.magnetic) || 0.22;
        el.addEventListener("mousemove", function (e) {
          var r = el.getBoundingClientRect();
          gsap.to(el, { x: (e.clientX - (r.left + r.width / 2)) * str,
            y: (e.clientY - (r.top + r.height / 2)) * str, duration: 0.5, ease: "power3.out" });
        });
        el.addEventListener("mouseleave", function () {
          gsap.to(el, { x: 0, y: 0, duration: 0.6, ease: "elastic.out(1,0.4)" });
        });
      });

      /* ---------- Custom cursor ---------- */
      var cur = document.createElement("div");
      cur.className = "hg-cursor"; cur.innerHTML = '<span class="hg-cursor__label"></span>';
      document.body.appendChild(cur);
      var cx = window.innerWidth / 2, cy = window.innerHeight / 2, tx = cx, ty = cy;
      window.addEventListener("mousemove", function (e) { tx = e.clientX; ty = e.clientY; });
      gsap.ticker.add(function () {
        cx += (tx - cx) * 0.28; cy += (ty - cy) * 0.28;
        cur.style.transform = "translate(" + cx + "px," + cy + "px)";
      });
      document.querySelectorAll('a, button, [data-cursor], .button').forEach(function (el) {
        el.addEventListener("mouseenter", function () {
          cur.classList.add("is-active");
          if (el.dataset.cursor) { cur.classList.add("is-label"); cur.querySelector(".hg-cursor__label").textContent = el.dataset.cursor; }
        });
        el.addEventListener("mouseleave", function () {
          cur.classList.remove("is-active", "is-label");
        });
      });
      docEl.classList.add("hg-has-cursor");
    }

    /* ---------- Header shrink on scroll ---------- */
    var header = document.querySelector(".header, header-component, .header-wrapper");
    if (header) ScrollTrigger.create({
      start: "top -80",
      onUpdate: function (self) { docEl.classList.toggle("hg-scrolled", self.scroll() > 80); }
    });

    ScrollTrigger.refresh();
    window.addEventListener("load", function () { ScrollTrigger.refresh(); });
  });

  function initCountersFallback() {
    document.querySelectorAll("[data-count]").forEach(function (el) {
      el.textContent = parseFloat(el.dataset.count).toLocaleString();
    });
  }
})();
